import axios from 'axios';

import Decimal from 'app/src/data/base/Decimal';
import SalePayment from 'app/src/data/models/SalePayment';
import {
  InvoiceReference,
  InvoiceReferenceResponse,
} from 'root/apps/QuoteMachineIntegration/state';
import { DataWithMetadata } from 'root/apps/QuoteMachineIntegration/state/DataWithMetadata';
import Employee from 'root/apps/QuoteMachineIntegration/state/Employee';
import Invoice from 'root/apps/QuoteMachineIntegration/state/Invoice';
import {
  DepositPayment,
  InvoicePayment,
  InvoicingSalePayment,
} from 'root/apps/QuoteMachineIntegration/state/InvoicingPayload';
import Location from 'root/apps/QuoteMachineIntegration/state/Location';
import { PayableInvoice } from 'root/apps/QuoteMachineIntegration/state/PayableInvoice';
import { datadogRum } from 'root/datadog/datadogRum';

import { errorMessages } from '../components/InvoicingModal';

const BASE_URL = '/admin/invoicing/ls-invoices';

const parseInvoiceReference = (response: InvoiceReferenceResponse): InvoiceReference => {
  return {
    identifierNumber: response.attributes.identifierNumber,
    proposalURL: response.attributes.proposalURL,
    customerName: response.attributes.customerName,
    sendEmail: response.attributes.sendEmail,
    toEmailAddress: response.attributes.toEmailAddress,
    invoiceID: response.id,
  };
};

export default class InvoiceService {
  static async createNewInvoice(
    sale: Sale,
    payments: InvoicingSalePayment[],
    invoicePayments: InvoicePayment[],
    depositPayments: DepositPayment[],
    giftCardPayments: SalePayment[],
    depositToAdd: Decimal
  ) {
    try {
      const employee = new Employee(window.merchantos.session.getEmployee());
      const location = new Location(await this.getLocationData());
      const invoice = new Invoice(
        sale,
        employee,
        location,
        String(window.merchantos.account.id),
        payments,
        invoicePayments,
        depositPayments,
        giftCardPayments,
        depositToAdd
      );

      return axios.post(BASE_URL, invoice);
    } catch (error) {
      throw error;
    }
  }

  static updateInvoice(saleId: string, payload: InvoiceUpdate) {
    const url = `${BASE_URL}/${saleId}`;
    return axios.patch(url, payload);
  }

  static deleteInvoice(saleId: string) {
    const url = `${BASE_URL}/${saleId}`;
    return axios.delete(url);
  }

  static async getInvoiceReference(saleID: string) {
    try {
      const { data } = await axios.get<InvoiceReferenceResponse>(`${BASE_URL}/reference/${saleID}`);
      return parseInvoiceReference(data);
    } catch (error) {
      datadogRum.addError(error, {
        saleID,
        message: `Failed to fetch invoicing/reference/${saleID}`,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  static async getLocationData(): Promise<Shop | null> {
    const accountID = window.merchantos.account.id;
    const shopID = window.merchantos.session.getShop()?.shopID;

    try {
      const response = await axios.get(`/API/V3/Account/${accountID}/Shop/${shopID}.json`, {
        params: {
          load_relations: '["Contact"]',
        },
      });
      return response?.data?.Shop;
    } catch (error) {
      window.merchantos.error(errorMessages.FETCH_LOCATION_ERROR);
      return null;
    }
  }

  static async getPayableInvoices(customerID: string, creditAccountID: string) {
    try {
      const { data } = await axios.get<DataWithMetadata<PayableInvoice>>(
        `admin/invoicing/payable-invoices?customerId=${customerID}&creditAccountId=${creditAccountID}&currency=${window.merchantos.locale.currency_code}`
      );
      return data;
    } catch (error) {
      datadogRum.addError(error, {
        customerID,
        message: `Failed to fetch admin/invoicing/payable-invoices?customerId=${customerID}`,
      });
      throw error;
    }
  }

  static async getProposalToken(invoiceID: string): Promise<string> {
    window.showNotifier('working');
    try {
      const response = await axios.post(
        `/admin/invoicing/invoice/${invoiceID}/proposal/generate-merchant-token`
      );
      return response?.data?.token || '';
    } catch (error) {
      datadogRum.addError(error, {
        radID: window.merchantos.account.id,
        invoiceID,
        message: `Failed to refresh proposal for hash: ${invoiceID}`,
      });
      return '';
    } finally {
      window.hideNotifier('working');
    }
  }
}
